// src/runtime/KnowledgeEvolutionRuntime/validator.ts
// ============================================
// Knowledge Validator - 候选知识风险评估器
//
// 职责：判断每条候选知识的风险等级（低 / 中 / 高）与处理路径：
//   - 低风险 → 自动通过，直接写入正式知识库
//   - 中风险 → 标记待确认（needs_review），CEO 可一键确认
//   - 高风险 → 必须 CEO 确认（needs_ceo），未确认不入库
//
// 判定逻辑：
//   1. 命中高风险关键词（战略/财务/对外承诺/法规）→ high
//   2. 命中中风险关键词（客户/报价/对外表述）→ medium
//   3. 默认 low（内部操作性知识）
// ============================================

import {
  type CandidateKnowledge,
  type CandidateRiskLevel,
} from './types';
import config from './config';

export class KnowledgeValidator {
  /**
   * 评估并更新候选知识的风险等级
   *
   * @returns 返回更新后的候选知识数组（原地修改 + 返回）
   */
  validate(candidates: CandidateKnowledge[]): CandidateKnowledge[] {
    for (const c of candidates) {
      c.riskLevel = this.assess(c);
      // 根据风险等级确定初始状态
      if (c.riskLevel === 'low') c.status = 'pending'; // 待 Evolver/Writer 自动入库
      else if (c.riskLevel === 'medium') c.status = 'needs_review';
      else c.status = 'needs_ceo';
    }
    return candidates;
  }

  /** 评估单条候选知识风险等级 */
  assess(candidate: CandidateKnowledge): CandidateRiskLevel {
    const text = `${candidate.title} ${candidate.summary} ${candidate.content}`;

    // 高风险关键词命中（任意一个即判高）
    for (const kw of config.highRiskKeywords) {
      if (text.includes(kw)) return 'high';
    }

    // 中风险关键词命中（命中 ≥2 个判中，避免单次误判）
    let mediumHits = 0;
    for (const kw of config.mediumRiskKeywords) {
      if (text.includes(kw)) mediumHits++;
    }
    if (mediumHits >= 2) return 'medium';

    return 'low';
  }

  /** 判断是否允许自动入库（低风险 + 评分达标） */
  canAutoApprove(candidate: CandidateKnowledge): boolean {
    return (
      candidate.riskLevel === 'low' &&
      candidate.score >= config.autoApproveMinScore
    );
  }
}

export default KnowledgeValidator;
